import {
  ChevronLeft,
  Inbox,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Send,
} from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { EmptyState } from "../data-display/EmptyState";
import { StatusBadge } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { useIsCompact } from "@/hooks/use-mobile";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { ConversationContext } from "./ConversationContext";
import { ScheduledMessages } from "./ScheduledMessages";
import {
  FILTER_LABELS,
  markConversationRead,
  useInbox,
  type InboxConversation,
  type InboxFilter,
} from "./useInbox";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

const CHANNEL_ICONS = { sms: MessageSquare, email: Mail, call: Phone } as const;

interface Conversation {
  id: string;
  contact_id: number;
  channel: "sms" | "email" | "call";
  subject?: string | null;
  status: string;
  last_message_at: string;
}

interface Message {
  id: string;
  conversation_id: string;
  direction: "inbound" | "outbound";
  body: string;
  subject?: string | null;
  status: string;
  provider?: string | null;
  error?: string | null;
  created_at: string;
}

interface ContactRow {
  id: number;
  first_name: string;
  last_name: string;
}

const MESSAGE_STATUS_TONES = {
  queued: "info",
  sent: "info",
  delivered: "success",
  failed: "danger",
  received: "neutral",
  logged: "neutral",
  draft: "neutral",
} as const;

/**
 * Unified inbox across SMS, email and calls.
 *
 * Conversations and messages are Gravun-owned tables; sending dispatches
 * through the workspace's connected provider adapters and fails honestly (with
 * guidance) when none exists. Logging records communication that happened
 * outside Gravun.
 *
 * The list reads the `inbox_conversations` view, so unread counts, the last
 * snippet, the assignee and the contact name arrive in ONE query rather than a
 * lookup per row. Filters are applied in SQL for the same reason: "unread"
 * must mean every unread thread, not the unread ones that happen to be on the
 * first page.
 */
export const InboxPage = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<InboxFilter>("all");
  // On a phone or tablet portrait there is only room for one pane, so the
  // inbox behaves like every other mobile inbox: list, then thread, with a
  // way back. Showing both stacked would bury the conversation below the
  // entire list.
  const isCompact = useIsCompact();

  const { data: conversations, isPending, refetch } = useInbox(filter);
  const list = conversations ?? [];

  const selected =
    list.find((c) => c.id === selectedId) ??
    (isCompact ? null : list[0]) ??
    null;

  const open = async (conversation: InboxConversation) => {
    setSelectedId(conversation.id);
    if (conversation.unread_count > 0) {
      // Opening is reading. Done before the refetch so the badge clears
      // immediately rather than after a round trip.
      await markConversationRead(conversation.id);
      refetch();
    }
  };

  const unreadTotal = list.reduce((sum, c) => sum + c.unread_count, 0);

  return (
    <>
      <ListPageHeader
        title="Inbox"
        subtitle="Every text, email and call with your customers in one place"
        actions={<NewConversationDialog onCreated={setSelectedId} />}
      />

      <div className="mb-3 flex min-w-0 gap-1 overflow-x-auto pb-1">
        {(Object.keys(FILTER_LABELS) as InboxFilter[]).map((key) => (
          <button
            key={key}
            onClick={() => {
              setFilter(key);
              setSelectedId(null);
            }}
            className={cn(
              "h-8 shrink-0 rounded-md px-3 text-[12.5px] font-medium transition-colors",
              filter === key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {FILTER_LABELS[key]}
            {key === "unread" && unreadTotal > 0 ? ` (${unreadTotal})` : ""}
          </button>
        ))}
      </div>

      {/* Scheduled is a queue of pending SENDS, not a list of conversations,
          so it gets its own surface rather than being squeezed into a filter
          over the thread list. */}
      {filter === "scheduled" ? (
        <ScheduledMessages />
      ) : isPending ? (
        <p className="py-12 text-center text-[13px] text-muted-foreground">
          Loading conversations…
        </p>
      ) : list.length === 0 ? (
        <Card className="py-16">
          <EmptyState
            icon={Inbox}
            title={
              filter === "all"
                ? "No conversations yet"
                : `Nothing ${FILTER_LABELS[filter].toLowerCase()}`
            }
            description={
              filter === "all"
                ? "Start a conversation with a contact, texts, emails and call logs all live here."
                : "Try another filter to see the rest of your inbox."
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[290px_1fr_240px]">
          <Card
            className={cn(
              "h-fit overflow-hidden py-0",
              isCompact && selected ? "hidden" : undefined,
            )}
          >
            {list.map((conversation) => {
              const Icon = CHANNEL_ICONS[conversation.channel] ?? Inbox;
              const isUnread = conversation.unread_count > 0;
              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => open(conversation)}
                  className={cn(
                    "flex w-full items-center gap-3 border-b border-border-subtle px-4 py-3 text-left transition-colors last:border-0",
                    selected?.id === conversation.id
                      ? "bg-surface-muted"
                      : "hover:bg-surface-muted/60",
                  )}
                >
                  <div
                    className={cn(
                      "flex size-8 shrink-0 items-center justify-center rounded-full",
                      isUnread
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/10 text-primary",
                    )}
                  >
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        "truncate text-[13.5px] text-foreground",
                        isUnread ? "font-semibold" : "font-medium",
                      )}
                    >
                      {conversation.contact_name || "Unknown contact"}
                    </p>
                    <p className="truncate text-[12px] text-muted-foreground">
                      {conversation.snippet ||
                        conversation.subject ||
                        conversation.channel.toUpperCase()}
                    </p>
                    {conversation.assignee_name ? (
                      <p className="truncate text-[11px] text-muted-foreground">
                        {conversation.assignee_name}
                      </p>
                    ) : null}
                  </div>
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(
                        conversation.last_message_at,
                      ).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    {isUnread ? (
                      <span className="rounded-full bg-primary px-1.5 text-[10.5px] font-semibold text-primary-foreground">
                        {conversation.unread_count}
                      </span>
                    ) : conversation.awaiting_reply ? (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[10.5px] font-medium text-amber-800">
                        Reply
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </Card>

          {selected ? (
            <div className="flex min-w-0 flex-col gap-2">
              {isCompact ? (
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="flex w-fit items-center gap-1 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronLeft className="size-4" />
                  All conversations
                </button>
              ) : null}
              <ConversationThread
                key={selected.id}
                conversation={selected}
                contactName={selected.contact_name}
              />
              {/* On a phone the context sits under the thread rather than in a
                  third column there is no room for. */}
              {isCompact ? (
                <Card className="mt-2 px-4 py-3">
                  <ConversationContext
                    contactId={selected.contact_id}
                    contactName={selected.contact_name}
                    assignedTo={selected.assigned_to}
                    conversationId={selected.id}
                    doNotContact={selected.do_not_contact}
                  />
                </Card>
              ) : null}
            </div>
          ) : null}

          {selected && !isCompact ? (
            <Card className="h-fit px-4 py-3">
              <ConversationContext
                contactId={selected.contact_id}
                contactName={selected.contact_name}
                assignedTo={selected.assigned_to}
                conversationId={selected.id}
                doNotContact={selected.do_not_contact}
              />
            </Card>
          ) : null}
        </div>
      )}
    </>
  );
};

const NewConversationDialog = ({
  onCreated,
}: {
  onCreated: (id: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [contactId, setContactId] = useState("");
  const [channel, setChannel] = useState("sms");
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending }] = useCreate();

  const { data: contacts } = useGetList<ContactRow>("contacts_summary", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });

  const submit = () => {
    if (!contactId) return;
    create(
      "conversations",
      {
        data: {
          contact_id: Number(contactId),
          channel,
          workspace_id: getActiveWorkspaceId(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: (record: Conversation) => {
          setOpen(false);
          onCreated(record.id);
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not start the conversation",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1 size-3.5" />
          New Conversation
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New conversation</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Contact
            <select
              className={inputClass}
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
            >
              <option value="">Choose a contact…</option>
              {(contacts ?? []).map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.first_name} {contact.last_name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Channel
            <select
              className={inputClass}
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            >
              <option value="sms">Text message</option>
              <option value="email">Email</option>
              <option value="call">Call</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={isPending || !contactId} onClick={submit}>
              Start
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ConversationThread = ({
  conversation,
  contactName,
}: {
  // Accepts the richer inbox row; only the shared fields are used here.
  conversation: Conversation | InboxConversation;
  contactName: string;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const [body, setBody] = useState("");
  const [mode, setMode] = useState<"send" | "log">("send");
  const [logDirection, setLogDirection] = useState<"inbound" | "outbound">(
    "inbound",
  );

  const { data: messages } = useGetList<Message>("messages", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "created_at", order: "ASC" },
    filter: { conversation_id: conversation.id },
  });

  const { mutate: compose, isPending } = useMutation({
    mutationFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("compose_message", {
        p_conversation_id: conversation.id,
        p_body: body.trim(),
        p_subject: null,
        p_mode: mode,
        p_direction: mode === "log" ? logDirection : "outbound",
      });
      if (error) throw error;
      return data as Message;
    },
    onSuccess: (message) => {
      setBody("");
      if (message.status === "failed") {
        notify(message.error ?? "Sending failed", { type: "warning" });
      } else {
        notify(mode === "log" ? "Message logged" : "Message queued");
      }
      refresh();
    },
    onError: (error: Error) =>
      notify(error.message ?? "Could not save the message", {
        type: "error",
      }),
  });

  const toggleStatus = () =>
    update(
      "conversations",
      {
        id: conversation.id,
        data: { status: conversation.status === "open" ? "closed" : "open" },
        previousData: conversation,
      },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );

  return (
    <Card className="flex h-[calc(100dvh-320px)] min-h-[360px] flex-col overflow-hidden py-0 lg:h-[calc(100dvh-220px)] lg:min-h-[420px]">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <p className="text-[14.5px] font-semibold">{contactName}</p>
          <p className="text-[12px] uppercase tracking-wide text-muted-foreground">
            {conversation.channel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge
            label={conversation.status === "open" ? "Open" : "Closed"}
            tone={conversation.status === "open" ? "info" : "neutral"}
          />
          <Button variant="outline" size="sm" onClick={toggleStatus}>
            {conversation.status === "open" ? "Close" : "Reopen"}
          </Button>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {(messages ?? []).map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex flex-col gap-1",
              message.direction === "outbound" ? "items-end" : "items-start",
            )}
          >
            <div
              className={cn(
                "max-w-[75%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-[13.5px] leading-snug",
                message.direction === "outbound"
                  ? "rounded-br-md bg-primary text-primary-foreground"
                  : "rounded-bl-md bg-surface-muted text-foreground",
              )}
            >
              {message.body}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span>
                {new Date(message.created_at).toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
              <StatusBadge
                label={message.status}
                tone={
                  MESSAGE_STATUS_TONES[
                    message.status as keyof typeof MESSAGE_STATUS_TONES
                  ] ?? "neutral"
                }
              />
            </div>
            {message.status === "failed" && message.error ? (
              <p className="max-w-[75%] text-[11.5px] text-destructive">
                {message.error}
              </p>
            ) : null}
          </div>
        ))}
        {(messages ?? []).length === 0 ? (
          <p className="py-6 text-center text-[13px] text-muted-foreground">
            No messages yet.
          </p>
        ) : null}
      </div>

      <div className="border-t border-border px-4 py-3">
        <div className="mb-2 flex items-center gap-1.5 text-[12px]">
          <button
            type="button"
            onClick={() => setMode("send")}
            className={cn(
              "h-6 rounded-full border px-2.5 font-medium transition-colors",
              mode === "send"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            Send
          </button>
          <button
            type="button"
            onClick={() => setMode("log")}
            className={cn(
              "h-6 rounded-full border px-2.5 font-medium transition-colors",
              mode === "log"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            Log
          </button>
          {mode === "log" ? (
            <select
              className="h-6 rounded-md border border-input bg-transparent px-1.5 text-[12px] outline-none"
              value={logDirection}
              onChange={(e) =>
                setLogDirection(e.target.value as "inbound" | "outbound")
              }
            >
              <option value="inbound">They said</option>
              <option value="outbound">We said</option>
            </select>
          ) : null}
        </div>
        <div className="flex items-end gap-2">
          <textarea
            className="min-h-10 flex-1 resize-none rounded-md border border-input bg-transparent px-2.5 py-2 text-[13.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder={
              mode === "send" ? `Message ${contactName}…` : "What was said…"
            }
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            size="icon"
            disabled={isPending || !body.trim()}
            onClick={() => compose()}
          >
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
